import { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { Loader2 } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Textarea,
} from "@f2e/ui/components/base"

// IMPORTANT: Must match VERSION_NOTE_MAX_LENGTH in codeer/agents/schemas.py
const VERSION_NOTE_MAX_LENGTH = 500

export interface VersionNoteDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (versionNote: string) => void
  onCancel: () => void
  isLoading?: boolean
}

export const VersionNoteDialog = ({
  open,
  onOpenChange,
  onConfirm,
  onCancel,
  isLoading = false,
}: VersionNoteDialogProps) => {
  const { t } = useTranslation()
  const [versionNote, setVersionNote] = useState("")

  // Reset version note when dialog opens
  useEffect(() => {
    if (open) {
      setVersionNote("")
    }
  }, [open])

  const handleConfirm = () => {
    onConfirm(versionNote)
  }

  // Handle dialog close via Escape key or clicking outside
  const handleOpenChange = (newOpen: boolean) => {
    if (isLoading && !newOpen) return // Prevent closing while loading

    if (!newOpen) {
      onCancel()
    }
    onOpenChange(newOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {t("workspace.agent.versionNote.dialog.title", "Save Version")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "workspace.agent.versionNote.dialog.description",
              "Add a note for this version (optional)"
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <Textarea
            value={versionNote}
            onChange={(e) => setVersionNote(e.target.value)}
            placeholder={t(
              "workspace.agent.versionNote.dialog.placeholder",
              "Describe what changed..."
            )}
            className="min-h-[100px] resize-none"
            disabled={isLoading}
            maxLength={VERSION_NOTE_MAX_LENGTH}
          />
          <div className="text-xs text-muted-foreground text-right mt-1">
            {versionNote.length}/{VERSION_NOTE_MAX_LENGTH}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={isLoading}>
            {t("workspace.agent.versionNote.dialog.cancel", "Cancel")}
          </Button>
          <Button onClick={handleConfirm} disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="size-4 mr-2 animate-spin" />
                {t("workspace.agent.versionNote.dialog.saving", "Saving...")}
              </>
            ) : (
              t("workspace.agent.versionNote.dialog.confirm", "Save")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
